import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { dateIt, STATI, num, euro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { StoricoOre } from "@/components/StoricoOre";
import { Clock, Save } from "lucide-react";
import { ConfermaMultiGiorno, verificaLimitiOre } from "@/components/SelettoreGiorni";
import { IntestazioneOre, INTESTAZIONE_ORE_INIZIALE, giorniEffettivi } from "@/components/IntestazioneOre";
import { AvvisoOreMancanti } from "@/components/AvvisoOreMancanti";
import { AssenzeForm } from "@/components/AssenzeForm";
import { PannelloDipendentiGiorno } from "@/components/PannelloDipendentiGiorno";


export default function OreRapide() {
  const qc = useQueryClient();
  const [testata, setTestata] = useState(INTESTAZIONE_ORE_INIZIALE());
  const { giorno, dipendenti_ids: dipendentiIds, luogo } = { giorno: testata.data, ...testata };
  const [pendingBulk, setPendingBulk] = useState(null);
  const [ore, setOre] = useState({});
  const [oreStr, setOreStr] = useState({});
  const { data: impostazioni } = useQuery({
    queryKey: ["impostazioni"],
    queryFn: async () => (await api.get("/impostazioni")).data,
  });

  const { data: commesse = [] } = useQuery({
    queryKey: ["destinazioni"],
    queryFn: async () => (await api.get("/destinazioni")).data,
  });
  const { data: dipendenti = [] } = useQuery({
    queryKey: ["dipendenti"],
    queryFn: async () => (await api.get("/dipendenti")).data,
  });

  const attiveTutte = commesse.filter((c) => c.stato === "aperta" || c.stato === "in_corso");
  const [filtroCommessa, setFiltroCommessa] = useState("tutte");
  const attive = filtroCommessa === "tutte" ? attiveTutte : attiveTutte.filter((c) => c.id === filtroCommessa);

  const bulkMut = useMutation({
    mutationFn: async ({ giorni, ...payload }) => {
      const results = [];
      const ordinati = [...giorni].sort();
      for (const [i, data] of ordinati.entries()) {
        const nonUltimo = i < ordinati.length - 1;
        results.push((await api.post("/ore/bulk", { ...payload, data, trasferta_notte: !!payload.trasferta && nonUltimo, cena_trasferta: !!payload.cena_trasferta && nonUltimo })).data);
      }
      return results.reduce((a, r) => ({ ...a, inserite: a.inserite + (r.inserite || 0), pasti: a.pasti + (r.pasti_inseriti || 0), cene: a.cene + (r.cene_inserite || 0), notti: a.notti + (r.notti_inserite || 0) }), { inserite: 0, pasti: 0, cene: 0, notti: 0, giorni: giorni.length });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["commesse"] });
      qc.invalidateQueries({ queryKey: ["prest-riepilogo"] });
      qc.invalidateQueries({ queryKey: ["prest-dashboard"] });
      qc.invalidateQueries({ queryKey: ["controllo-giornaliero"] });
      qc.invalidateQueries({ queryKey: ["stato-giorno"] });
      const extra = [res.pasti ? `${res.pasti} pasti cantiere` : "", res.cene ? `${res.cene} cene trasferta` : "", res.notti ? `${res.notti} notti` : ""].filter(Boolean).join(", ");
      toast.success(`${res.inserite} registrazioni ore${extra ? ` + ${extra}` : ""} salvate${res.giorni > 1 ? ` su ${res.giorni} giorni` : ""}`);
      setOre({});
      setOreStr({});
      setTestata((t) => ({ ...t, note: "", giorni: [], trasferta: false, cena_trasferta: false }));
      setPendingBulk(null);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const totaleOre = Object.values(ore).reduce((s, v) => s + (Number(v) || 0), 0);
  const totaleStr = Object.values(oreStr).reduce((s, v) => s + (Number(v) || 0), 0);
  const conOrario = !!(testata.ora_inizio && testata.ora_fine);
  const extraPayload = { note: testata.note, pasto_cantiere: testata.pasto_cantiere, cena_trasferta: testata.cena_trasferta, trasferta: testata.trasferta, ora_inizio: conOrario ? testata.ora_inizio : null, ora_fine: conOrario ? testata.ora_fine : null };

  const submit = () => {
    if (dipendentiIds.length === 0) {
      toast.error("Seleziona almeno un dipendente");
      return;
    }
    if (!luogo) {
      toast.error("Seleziona il luogo di lavoro (Officina o Cantiere)");
      return;
    }
    const oltre = attive.find((c) => Number(ore[c.id]) > 8);
    if (oltre) {
      toast.error(`Le ore ordinarie non possono superare 8 al giorno (commessa ${oltre.codice})`);
      return;
    }
    const ids = new Set([...Object.keys(ore), ...Object.keys(oreStr)]);
    const righe = dipendentiIds.flatMap((dipendente_id) =>
      [...ids].map((commessa_id) => ({ commessa_id, dipendente_id, tipo: testata.tipo || "ordinario", ore: Number(ore[commessa_id]) || 0, ore_straordinarie: Number(oreStr[commessa_id]) || 0 })),
    ).filter((r) => r.ore > 0 || r.ore_straordinarie > 0 || (conOrario && ore[r.commessa_id] !== undefined && ore[r.commessa_id] !== ""));
    if (righe.length === 0) {
      toast.error(conOrario ? "Indica la commessa: scrivi 0 nelle ore della commessa per usare le ore calcolate dal turno" : "Inserisci almeno un'ora su una commessa");
      return;
    }
    const giorni = giorniEffettivi(testata);
    const verificaRighe = giorni.flatMap((data) => righe.map((r) => ({ dipendente_id: r.dipendente_id, data, ore: r.ore, ore_straordinarie: r.ore_straordinarie })));
    verificaLimitiOre(api, verificaRighe)
      .then((v) => {
        if (!v.ok) {
          v.bloccate.forEach((b) => toast.error(b.messaggio, { duration: 9000 }));
          return;
        }
        if (giorni.length > 1 || dipendentiIds.length > 1 || v.avvisi.length > 0) {
          setPendingBulk({ giorni, luogo, righe, avvisi: v.avvisi, ...extraPayload });
          return;
        }
        bulkMut.mutate({ giorni, luogo, righe, ...extraPayload });
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  return (
    <div className="space-y-6">
      <ConfermaMultiGiorno
        open={!!pendingBulk}
        onOpenChange={(v) => !v && setPendingBulk(null)}
        onConfirm={() => pendingBulk && bulkMut.mutate({ giorni: pendingBulk.giorni, luogo: pendingBulk.luogo, righe: pendingBulk.righe, ...extraPayload })}
        giorni={pendingBulk?.giorni || []}
        avvisi={pendingBulk?.avvisi || []}
        righePerGiorno={pendingBulk?.righe.length || 1}
        dipendente={dipendenti.filter((x) => dipendentiIds.includes(x.id)).map((d) => `${d.nome} ${d.cognome}`.trim()).join(", ")}
        loading={bulkMut.isPending}
      />
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Ore di Lavoro
        </h1>
        <p className="text-slate-500 mt-1">
          Registra le ore di giornata di uno o più dipendenti su più commesse in una volta sola, con pasti, cene e notti fuori sede.
        </p>
      </div>

      <AvvisoOreMancanti />

      <PannelloDipendentiGiorno data={testata.data} />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <IntestazioneOre value={testata} onChange={setTestata} dipendenti={dipendenti} impostazioni={impostazioni} testid="ore-rapide" />
      </div>

      <AssenzeForm dipendentiIds={dipendentiIds} giorni={giorniEffettivi(testata)} />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Clock className="h-5 w-5 text-sky-600" /> Commesse e prestazioni attive
            <Select value={filtroCommessa} onValueChange={setFiltroCommessa}>
              <SelectTrigger className="h-8 w-[260px] text-sm font-normal ml-2" data-testid="ore-rapide-filtro-commessa">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutte le commesse e prestazioni attive</SelectItem>
                {attiveTutte.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.codice} — {c.descrizione}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </h2>
          <span className="text-sm text-slate-500">
            Totale giornata: <span className="font-semibold text-slate-800">{num(totaleOre, 1)} h</span>
            {totaleStr > 0 && <span className="ml-2 text-primary" data-testid="ore-rapide-totale-straord">+ {num(totaleStr, 1)} h straord.</span>}
          </span>
        </div>
        {attive.length === 0 ? (
          <div className="py-12 text-center text-slate-400">Nessuna commessa attiva.</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {attive.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-4 px-5 py-3"
                data-testid={`ore-rapide-riga-${c.codice}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-500">{c.codice}</span>
                    {c.tipo === "prestazione" ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium border bg-orange-50 text-primary border-orange-200" data-testid={`ore-rapide-prest-${c.id}`}>Prestazione manodopera</span>
                    ) : (
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${STATI[c.stato]?.cls}`}
                    >
                      {STATI[c.stato]?.label}
                    </span>
                    )}
                  </div>
                  <div className="text-sm font-medium text-slate-800 truncate">{c.descrizione}</div>
                  <div className="text-xs text-slate-400">{c.cliente_nome}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="8"
                    step="0.5"
                    placeholder="0"
                    value={ore[c.id] ?? ""}
                    onChange={(e) => setOre((o) => ({ ...o, [c.id]: e.target.value }))}
                    className={`w-24 text-right ${Number(ore[c.id]) > 8 ? "border-red-500 text-red-600" : ""}`}
                    data-testid={`ore-rapide-input-${c.codice}`}
                  />
                  <span className="text-sm text-slate-400 w-8">ord.</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="0"
                    value={oreStr[c.id] ?? ""}
                    onChange={(e) => setOreStr((o) => ({ ...o, [c.id]: e.target.value }))}
                    className="w-24 text-right ml-2 border-orange-200"
                    title="Ore straordinarie"
                    data-testid={`ore-rapide-straord-${c.codice}`}
                  />
                  <span className="text-sm text-primary w-12">straord.</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={submit}
          disabled={bulkMut.isPending}
          className="h-11 px-6"
          data-testid="ore-rapide-submit"
        >
          <Save className="h-4 w-4 mr-2" /> Salva ore{testata.trasferta ? " e trasferte" : ""} ({giorniEffettivi(testata).length > 1 ? `${giorniEffettivi(testata).length} giorni` : dateIt(giorno)})
        </Button>
      </div>
      <StoricoOre dipendenti={dipendenti} commesse={commesse} />
    </div>
  );
}
